import { VoiceCallPanel } from "./voice/VoiceSurface"
/**
 * WildLark Main Entry Point
 *
 * This is the standalone app entry point.
 * For embedding in other apps, import from './index' instead.
 */

import { Loader2 } from "lucide-react"
import { observer } from "mobx-react"
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter, Navigate, Route, useLocation, useNavigate } from "react-router"
import {
    CodeBaseRoute,
    CodeWorkspaceCreateRoute,
    CodeWorkspaceDiffLabRoute,
    CodeWorkspaceRoute,
    CodeWorkspaceSettingsRoute,
    CodeWorkspaceTaskCreateRoute,
    CodeWorkspaceTaskCreatingRoute,
    CodeWorkspaceTaskRoute,
} from "./Routes"
import { ConnectionIssueView } from "./components/computers/ConnectionIssueView"
import { showControllerPermissionPrompt } from "./voice/controller"
import { createComputerBackend } from "./computers/backend"
import { type ComputerContextValue, ComputerProvider, type PairingSession, type ShareRequest } from "./computers/context"
import { offlineOverview, refreshComputerOverviews } from "./computers/overview"
import { readOverviewCache, writeOverviewCache } from "./computers/overviewCache"
import {
    type PairedComputer,
    type PairingInvite,
    createPairingInviteUrl,
    currentBrowserDeviceName,
    pairWithComputer,
    readPairingInvite,
} from "./computers/pairing"
import { type PersonRecord, grantsForLevel, readPeople, removePerson, shareMessage, writePerson } from "./computers/people"
import type { AddRemoteComputerInput, ComputerListState, ComputerOverview, ComputerScope, OverviewBatchResult } from "./computers/types"
import { setCodeStorageScope } from "./constants"
import { PRODUCT_NAME } from "./product"
import {
    connectNestConnection,
    createNestClient,
    installNestRpcLogDebugApi,
    isNestUnauthorizedError,
    nestClient,
    setNestConnectionProvider,
} from "./nest/client"
import type { NestConnectionFailure } from "./nest/connectionFailure"
import { nestRouteLabel, shareableNestRoutes } from "./nest/routes"
import { resetCodeModuleCapabilities } from "./nestAPIs/capabilities"
import { logElectronBridgeCompatibility } from "./electronAPIs/capabilities"
import { resetPlatformInfo } from "./nestAPIs/platform"
import { ConnectComputerPage } from "./pages/ConnectComputerPage"
import { getPlatformAdapter } from "./platform/adapter"
import { LiveStatusCoordinator } from "./platform/liveStatusCoordinator"
import { recordMobileReconciliationSuccess } from "./platform/mobileDiagnostics"
import { getMobileLiveStatusPolicy } from "./platform/mobilePolicy"
import { readNotificationRouteUrl } from "./platform/notifications"
import { PushEnrollmentCoordinator, setActivePushEnrollmentCoordinator } from "./platform/pushEnrollment"
import { createPublisherClaim, publishMobileRunState } from "./platform/pushPublisher"
import { startStatusBarThemeCoordinator } from "./platform/statusBarTheme"
import type { NotificationRoute } from "./platform/types"
import { TaskCreateRoutes } from "./layout/TaskCreateRoutes"
import { useResolvedTheme } from "./hooks/useResolvedTheme"
import { codeRoutes, getNewTaskRouteState } from "./routing"
import { CodeStoreProvider, useCodeStore } from "./store/context"
import { CodeStore, type CodeStoreConfig } from "./store/store"
import { installProductModules, ProductModuleGlobalSurfaces } from "./modules/registry"
import { CodeModalProvider } from "./components/ui/CodeModalProvider"
import "./index.css"

installNestRpcLogDebugApi()
installProductModules()

const initialWindowPairingInvite = typeof window === "undefined" ? null : readPairingInvite(window.location.href)
if (initialWindowPairingInvite) {
    window.history.replaceState(window.history.state, "", "/")
}

// Default user for standalone mode
const getDefaultUser = () => ({
    id: "local-user",
    name: "Local User",
    email: "local@wildlark.dev",
})

// Lazy-bound router navigate — set by App once inside <BrowserRouter>
let routerNavigate: ReturnType<typeof useNavigate> | null = null

const COMPUTER_SCOPE_KEY = "wildlark:computer-scope:v1"
const COMPUTER_PATHS_KEY = "wildlark:computer-paths:v1"
const CONNECT_TIMEOUT_MS = 10_000
const REMOTE_CONNECT_TIMEOUT_MS = 4_000
const REMOTE_DISCONNECT_GRACE_MS = 3_000
const OVERVIEW_SWITCH_REFRESH_MIN_MS = 5_000
const OVERVIEW_FOCUS_REFRESH_MIN_MS = 60_000

type ConnectionIssue =
    | { nodeId: string; name: string; reason: "offline"; failure: NestConnectionFailure | null }
    | { nodeId: string; name: string; reason: "revoked" }

const ThemedModalProvider = observer(function ThemedModalProvider({ children }: { children: ReactNode }) {
    const store = useCodeStore()
    const themeSetting = store.personalSettingsStore?.settings.current.theme ?? "system"
    const themeClass = useResolvedTheme(themeSetting)

    return (
        <div className={`code-theme ${themeClass} contents`}>
            <CodeModalProvider>{children}</CodeModalProvider>
        </div>
    )
})

function createCodeStore(): CodeStore {
    const storeConfig: CodeStoreConfig = {
        getCurrentUser: getDefaultUser,
        navigateToTask: (workspaceId: string, taskId: string) => {
            const path = codeRoutes.CodeWorkspaceTask.makePath({
                workspaceId,
                taskId,
            })
            if (routerNavigate) routerNavigate(path)
            else window.history.replaceState(window.history.state, "", path)
        },
    }
    return new CodeStore(storeConfig)
}

function readStoredScope(state: ComputerListState): ComputerScope {
    return state.activeNodeId
}

function readComputerPaths(): Record<string, string> {
    try {
        const value: unknown = JSON.parse(window.localStorage.getItem(COMPUTER_PATHS_KEY) ?? "{}")
        if (typeof value !== "object" || value === null || Array.isArray(value)) return {}
        return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === "string"))
    } catch {
        return {}
    }
}

function storeComputerPath(nodeId: string, pathname: string): void {
    const paths = readComputerPaths()
    paths[nodeId] = pathname
    window.localStorage.setItem(COMPUTER_PATHS_KEY, JSON.stringify(paths))
}

function App() {
    routerNavigate = useNavigate()
    const navigate = useNavigate()
    const location = useLocation()
    const backend = useMemo(() => createComputerBackend(), [])
    const platform = useMemo(() => getPlatformAdapter(), [])
    const nativeMobile = platform.kind === "ios" || platform.kind === "android"

    useEffect(() => nestClient.registerPermissionHandler((request) => showControllerPermissionPrompt(request, nestClient.getDiagnostics().nodeId)), [])
    const pushEnrollment = useMemo(
        () => (platform.kind === "ios" || platform.kind === "android" ? new PushEnrollmentCoordinator(platform, platform.kind) : null),
        [platform]
    )
    const initialization = useRef<Promise<void> | null>(null)
    const pairingInFlight = useRef<string | null>(null)
    const runtimeRef = useRef<{ nodeId: string; store: CodeStore } | null>(null)
    const computerStateRef = useRef<ComputerListState | null>(null)
    const recoveringUnauthorized = useRef(false)
    const remoteDisconnectTimer = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null)
    const foregroundRecovery = useRef<Promise<void> | null>(null)
    const liveStatusCoordinatorRef = useRef<LiveStatusCoordinator | null>(null)
    const [computerState, setComputerState] = useState<ComputerListState | null>(null)
    const [people, setPeople] = useState<PersonRecord[]>(() => (typeof window === "undefined" ? [] : readPeople()))
    const [scope, setScope] = useState<ComputerScope>("")
    const [codeStore, setCodeStore] = useState<CodeStore | null>(null)
    const [overviews, setOverviews] = useState<Record<string, ComputerOverview>>(() => readOverviewCache())
    const [overviewBatch, setOverviewBatch] = useState<OverviewBatchResult | null>(null)
    const [busy, setBusy] = useState(false)
    const [computerError, setComputerError] = useState<string | null>(null)
    const [initError, setInitError] = useState<string | null>(null)
    const [accessIssue, setAccessIssue] = useState<string | null>(null)
    const [connectionIssue, setConnectionIssue] = useState<ConnectionIssue | null>(null)
    const [pendingNotificationRoute, setPendingNotificationRoute] = useState<NotificationRoute | null>(null)
    const [inviteStatus, setInviteStatus] = useState<{
        state: "connecting" | "error"
        error?: string
    } | null>(initialWindowPairingInvite ? { state: "connecting" } : null)

    useEffect(() => {
        const coordinator = startStatusBarThemeCoordinator(platform)
        return () => coordinator.remove()
    }, [platform])

    useEffect(() => {
        if (!pushEnrollment) return
        let disposed = false
        setActivePushEnrollmentCoordinator(pushEnrollment)
        void pushEnrollment.start().catch(() => {
            if (!disposed) setInitError("Mobile notification enrollment could not start. Core app features remain available.")
        })
        return () => {
            disposed = true
            setActivePushEnrollmentCoordinator(null)
            pushEnrollment.dispose()
        }
    }, [pushEnrollment])

    const initializeStore = useCallback(
        async (nodeId: string): Promise<CodeStore> => {
            const connection = await backend.getConnection(nodeId)
            setCodeStorageScope(connection.nodeId, connection.kind === "local")
            resetCodeModuleCapabilities()
            resetPlatformInfo()
            await connectNestConnection(nestClient, connection, {
                timeoutMs: connection.kind === "remote" ? REMOTE_CONNECT_TIMEOUT_MS : CONNECT_TIMEOUT_MS,
            })
            const store = createCodeStore()
            await store.initializeStores()
            runtimeRef.current = { nodeId: connection.nodeId, store }
            setCodeStore(store)
            return store
        },
        [backend]
    )

    const currentProfile = useCallback(() => {
        const profile = runtimeRef.current?.store.personalSettingsStore?.settings.current.profile
        return profile ? { name: profile.name, glyph: profile.glyph, color: profile.color } : undefined
    }, [])

    // A share is recorded as a person on top of the ordinary computer entry so
    // the sidebar, switcher, and overview can say whose computer it is.
    const recordPerson = useCallback((invite: PairingInvite, paired: PairedComputer) => {
        if (paired.access.summary.node && !invite.share) return
        // A linked colleague starts with no access; the invite says so explicitly.
        const level = invite.share?.level && paired.access.summary.level !== "operate" ? invite.share.level : paired.access.summary.level
        if (level === "none" && !invite.share) return
        const person: PersonRecord = {
            nodeId: paired.nodeId,
            personName: invite.share?.from ?? paired.nodeName,
            level,
            projectNames: invite.share?.project ? [invite.share.project] : [],
            addedAt: new Date().toISOString(),
            ...(invite.share?.glyph ? { glyph: invite.share.glyph } : {}),
            ...(invite.share?.color ? { color: invite.share.color } : {}),
            ...(paired.access.principalId ? { principalId: paired.access.principalId } : {}),
        }
        setPeople(writePerson(person))
    }, [])

    const acceptPairingInvite = useCallback(
        async (invite: PairingInvite): Promise<boolean> => {
            const inviteKey = `${invite.nodeId}\u0000${invite.code}`
            if (pairingInFlight.current === inviteKey) return false
            pairingInFlight.current = inviteKey
            setInviteStatus({ state: "connecting" })
            setBusy(true)
            let pairedNodeName = invite.nodeId
            let persisted = false
            try {
                const paired = await pairWithComputer({
                    ...invite,
                    expectedNodeId: invite.nodeId,
                    clientName: nativeMobile ? `${PRODUCT_NAME} on ${platform.kind === "ios" ? "iOS" : "Android"}` : currentBrowserDeviceName(),
                    profile: currentProfile(),
                })
                pairedNodeName = paired.name
                const { access: _access, nodeName: _nodeName, ...registration } = paired
                await backend.add(registration)
                await backend.activate(paired.nodeId)
                persisted = true
                recordPerson(invite, paired)

                runtimeRef.current?.store.disconnectAllStores()
                runtimeRef.current = null
                nestClient.close()
                setCodeStore(null)

                const state = await backend.list()
                setComputerState(state)
                await initializeStore(paired.nodeId)
                setScope(paired.nodeId)
                window.localStorage.setItem(COMPUTER_SCOPE_KEY, paired.nodeId)
                setInviteStatus(null)
                setAccessIssue(null)
                setConnectionIssue(null)
                setInitError(null)
                if (invite.pushClaim && pushEnrollment) {
                    try {
                        if (!paired.tokenId) throw new Error("The paired computer did not return a device identity")
                        await pushEnrollment.prepareClaim(paired.nodeId, paired.tokenId, invite.pushClaim)
                    } catch {
                        setComputerError("This computer paired successfully, but mobile notification enrollment needs to be retried with a new pairing link.")
                    }
                }
                navigate(codeRoutes.Code.makePath(), { replace: true })
                void platform.haptic("success")
                return true
            } catch (error) {
                setInviteStatus({
                    state: "error",
                    error: error instanceof Error ? error.message : String(error),
                })
                if (persisted) setConnectionIssue({ nodeId: invite.nodeId, name: pairedNodeName, reason: "offline", failure: null })
                return false
            } finally {
                if (pairingInFlight.current === inviteKey) pairingInFlight.current = null
                setBusy(false)
            }
        },
        [backend, currentProfile, initializeStore, nativeMobile, navigate, platform, pushEnrollment, recordPerson]
    )

    useEffect(() => {
        if (!nativeMobile) return
        let disposed = false
        let listener: Awaited<ReturnType<typeof platform.addUrlOpenListener>> | null = null
        void platform
            .addUrlOpenListener((url) => {
                if (disposed) return
                const invite = readPairingInvite(url)
                if (invite) {
                    void acceptPairingInvite(invite)
                    return
                }
                const notificationRoute = readNotificationRouteUrl(url)
                if (notificationRoute) setPendingNotificationRoute(notificationRoute)
            })
            .then((handle) => {
                if (disposed) void handle.remove()
                else listener = handle
            })
            .catch((error) => {
                if (!disposed) setInitError(`Could not listen for pairing links: ${error instanceof Error ? error.message : String(error)}`)
            })
        return () => {
            disposed = true
            if (listener) void listener.remove()
        }
    }, [acceptPairingInvite, nativeMobile, platform])

    useEffect(() => {
        if (platform.kind !== "android") return
        let disposed = false
        let listener: Awaited<ReturnType<typeof platform.addBackButtonListener>> | null = null
        void platform
            .addBackButtonListener(({ canGoBack }) => {
                if (canGoBack) {
                    navigate(-1)
                    return
                }
                const homePath = codeRoutes.Code.path
                if (location.pathname !== homePath) {
                    navigate(homePath)
                    return
                }
                void platform.minimizeApp()
            })
            .then((handle) => {
                if (disposed) void handle.remove()
                else listener = handle
            })
        return () => {
            disposed = true
            if (listener) void listener.remove()
        }
    }, [location.pathname, navigate, platform, scope])

    useEffect(() => {
        if (!initialization.current) {
            initialization.current = (async () => {
                if (platform.kind === "electron") await logElectronBridgeCompatibility()
                setNestConnectionProvider(() => backend.getConnection())
                let invite = initialWindowPairingInvite
                if (!invite && nativeMobile) {
                    const launchUrl = await platform.getLaunchUrl()
                    invite = launchUrl ? readPairingInvite(launchUrl) : null
                    const notificationRoute = launchUrl ? readNotificationRouteUrl(launchUrl) : null
                    if (notificationRoute) setPendingNotificationRoute(notificationRoute)
                }
                if (invite && (await acceptPairingInvite(invite))) return
                let state = await backend.list()
                setComputerState(state)
                if (state.computers.length === 0) return
                let nextScope: ComputerScope = readStoredScope(state)
                setScope(nextScope)
                try {
                    await initializeStore(state.activeNodeId)
                } catch (error) {
                    const localFallback = state.computers.find((computer) => computer.kind === "local" && computer.nodeId !== state.activeNodeId)
                    if (localFallback) {
                        nestClient.close()
                        await backend.activate(localFallback.nodeId)
                        await initializeStore(localFallback.nodeId)
                        setComputerState(await backend.list())
                        nextScope = localFallback.nodeId
                        setScope(nextScope)
                        window.localStorage.setItem(COMPUTER_SCOPE_KEY, nextScope)
                    } else if (isNestUnauthorizedError(error)) {
                        const revoked = state.computers.find((computer) => computer.nodeId === state.activeNodeId)
                        if (!revoked || revoked.kind === "local") throw error
                        nestClient.close()
                        if (nativeMobile) {
                            setConnectionIssue({ nodeId: revoked.nodeId, name: revoked.name, reason: "revoked" })
                            setAccessIssue(`Access to ${revoked.name} was revoked. Open a fresh pairing link from that computer to reconnect.`)
                            return
                        }
                        await backend.remove(revoked.nodeId)
                        state = await backend.list()
                        setComputerState(state)
                        setScope(state.computers.length > 0 ? state.activeNodeId : "")
                        window.localStorage.removeItem(COMPUTER_SCOPE_KEY)
                        setAccessIssue(`Access to ${revoked.name} was revoked. Open a fresh pairing link from that computer to reconnect.`)
                        return
                    } else {
                        const disconnected = state.computers.find((computer) => computer.nodeId === state.activeNodeId)
                        const failure = nestClient.getConnectionFailure()
                        if (disconnected?.kind === "remote") {
                            setConnectionIssue({ nodeId: disconnected.nodeId, name: disconnected.name, reason: "offline", failure })
                            return
                        }
                        throw error
                    }
                }
            })()
        }
        void initialization.current.catch((error) => setInitError(error instanceof Error ? error.message : String(error)))
    }, [acceptPairingInvite, backend, initializeStore, nativeMobile, navigate, platform])

    useEffect(() => {
        computerStateRef.current = computerState
    }, [computerState])

    useEffect(() => {
        if ((!nativeMobile && platform.kind !== "electron") || !codeStore) return
        const runtime = runtimeRef.current
        if (!runtime || runtime.store !== codeStore) return
        const computer = computerStateRef.current?.computers.find((candidate) => candidate.nodeId === runtime.nodeId)
        if (!computer) return

        const coordinator = new LiveStatusCoordinator({
            nodeId: runtime.nodeId,
            computerName: computer.name,
            store: codeStore,
            ...(nativeMobile ? { isRemoteLiveStatusEnabled: () => getMobileLiveStatusPolicy().liveStatusEnabled } : {}),
            ...(platform.kind === "electron"
                ? {
                      publishState: async (state: Parameters<typeof publishMobileRunState>[0]) => (await publishMobileRunState(state)).status === "published",
                  }
                : {}),
        })
        liveStatusCoordinatorRef.current = coordinator
        return () => {
            coordinator.dispose()
            if (liveStatusCoordinatorRef.current === coordinator) liveStatusCoordinatorRef.current = null
        }
    }, [codeStore, nativeMobile, platform.kind])

    useEffect(() => {
        if (!scope || !codeStore) return
        storeComputerPath(scope, getNewTaskRouteState(location)?.newTaskBackgroundPath ?? `${location.pathname}${location.search}`)
    }, [codeStore, location, scope])

    const overviewsRef = useRef(overviews)
    overviewsRef.current = overviews
    const overviewRefreshInFlight = useRef<Promise<void> | null>(null)
    const lastOverviewRefreshAt = useRef(0)
    const refreshOverviews = useCallback(async () => {
        if (!computerState || computerState.computers.length === 0) return
        if (overviewRefreshInFlight.current) return overviewRefreshInFlight.current
        const run = (async () => {
            const batch = await refreshComputerOverviews(backend, computerState.computers, overviewsRef.current)
            lastOverviewRefreshAt.current = Date.now()
            setOverviews(batch.overviews)
            writeOverviewCache(batch.overviews)
            setOverviewBatch(batch)
        })()
        overviewRefreshInFlight.current = run
        try {
            await run
        } finally {
            if (overviewRefreshInFlight.current === run) overviewRefreshInFlight.current = null
        }
    }, [backend, computerState])

    // Other computers' projects and recent tasks live in the sidebar now, so
    // their overviews refresh when a store comes up (launch or switch) and
    // when the window regains focus. No polling: the plan forbids keeping a
    // socket to every node, and a stale row says how old it is on hover.
    useEffect(() => {
        if (!codeStore || !computerState || computerState.computers.length < 2) return
        const refreshIfStale = (minIntervalMs: number) => {
            if (Date.now() - lastOverviewRefreshAt.current < minIntervalMs) return
            void refreshOverviews().catch((error: unknown) => console.warn("[computers] overview refresh failed", error))
        }
        refreshIfStale(OVERVIEW_SWITCH_REFRESH_MIN_MS)
        const onFocus = () => refreshIfStale(OVERVIEW_FOCUS_REFRESH_MIN_MS)
        window.addEventListener("focus", onFocus)
        return () => window.removeEventListener("focus", onFocus)
    }, [codeStore, computerState, refreshOverviews])

    const switchRuntime = useCallback(
        async (nodeId: string): Promise<void> => {
            const current = runtimeRef.current
            if (current?.nodeId === nodeId) return
            const target = await backend.getConnection(nodeId)
            const probe = createNestClient()
            try {
                await connectNestConnection(probe, target, {
                    timeoutMs: target.kind === "remote" ? REMOTE_CONNECT_TIMEOUT_MS : CONNECT_TIMEOUT_MS,
                })
                const status = await probe.requestMethod(
                    "system.initialize",
                    { clientName: PRODUCT_NAME, protocolVersion: 1 },
                    { timeoutMs: CONNECT_TIMEOUT_MS }
                )
                if (status.nodeId !== nodeId) throw new Error("The computer returned a different node identity")
            } finally {
                probe.close()
            }

            setBusy(true)
            setInitError(null)
            const previous = runtimeRef.current
            try {
                previous?.store.disconnectAllStores()
                nestClient.close()
                await backend.activate(nodeId)
                await initializeStore(nodeId)
                setComputerState(await backend.list())
            } catch (error) {
                if (previous) {
                    try {
                        await backend.activate(previous.nodeId)
                        nestClient.close()
                        await initializeStore(previous.nodeId)
                    } catch (recoveryError) {
                        setInitError(
                            `Computer switch failed and the previous connection could not be restored: ${recoveryError instanceof Error ? recoveryError.message : String(recoveryError)}`
                        )
                    }
                }
                throw error
            } finally {
                setBusy(false)
            }
        },
        [backend, initializeStore]
    )

    const openNotificationRoute = useCallback(
        async (route: NotificationRoute): Promise<void> => {
            const state = computerStateRef.current
            const computer = state?.computers.find((candidate) => candidate.nodeId === route.nodeId)
            if (!computer) {
                setComputerError("This notification belongs to a computer that is no longer paired with this phone.")
                navigate(codeRoutes.Code.makePath())
                return
            }

            await switchRuntime(route.nodeId)
            setScope(route.nodeId)
            window.localStorage.setItem(COMPUTER_SCOPE_KEY, route.nodeId)
            navigate(
                codeRoutes.CodeWorkspaceTask.makePath({
                    workspaceId: route.workspaceId,
                    taskId: route.taskId,
                })
            )
            void platform.haptic("selection")
        },
        [navigate, platform, switchRuntime]
    )

    useEffect(() => {
        if (!nativeMobile) return
        let disposed = false
        let listener: Awaited<ReturnType<typeof platform.addNotificationTapListener>> | null = null
        void platform
            .addNotificationTapListener((tap) => {
                if (!disposed) setPendingNotificationRoute(tap.route)
            })
            .then((handle) => {
                if (disposed) void handle.remove()
                else listener = handle
            })
            .catch((error) => {
                if (!disposed) setInitError(`Could not listen for notification taps: ${error instanceof Error ? error.message : String(error)}`)
            })
        return () => {
            disposed = true
            if (listener) void listener.remove()
        }
    }, [nativeMobile, platform])

    useEffect(() => {
        if (!nativeMobile) return
        let disposed = false
        let listener: Awaited<ReturnType<typeof platform.addForegroundNotificationListener>> | null = null
        void platform
            .addForegroundNotificationListener((notification) => {
                if (disposed) return
                void platform.haptic(notification.phase === "needs-input" ? "warning" : notification.phase === "succeeded" ? "success" : "error")
            })
            .then((handle) => {
                if (disposed) void handle.remove()
                else listener = handle
            })
            .catch((error) => {
                if (!disposed) setInitError(`Could not listen for foreground notifications: ${error instanceof Error ? error.message : String(error)}`)
            })
        return () => {
            disposed = true
            if (listener) void listener.remove()
        }
    }, [nativeMobile, platform])

    useEffect(() => {
        if (!pendingNotificationRoute || !computerState) return
        const paired = computerState.computers.some((computer) => computer.nodeId === pendingNotificationRoute.nodeId)
        if (paired && !codeStore) return

        const route = pendingNotificationRoute
        setPendingNotificationRoute(null)
        void openNotificationRoute(route).catch((error) => {
            setComputerError(error instanceof Error ? error.message : String(error))
        })
    }, [codeStore, computerState, openNotificationRoute, pendingNotificationRoute])

    const recoverUnauthorizedConnection = useCallback(async () => {
        if (recoveringUnauthorized.current) return
        const state = computerStateRef.current
        const active = runtimeRef.current
        if (!state || !active) return
        const revoked = state.computers.find((computer) => computer.nodeId === active.nodeId)
        if (!revoked || revoked.kind === "local") {
            setInitError("The local Nest credential is no longer authorized. Restart WildLark to restore the local connection.")
            return
        }

        recoveringUnauthorized.current = true
        const fallback = state.computers.find((computer) => computer.kind === "local" && computer.nodeId !== revoked.nodeId)
        try {
            if (nativeMobile) {
                active.store.disconnectAllStores()
                runtimeRef.current = null
                nestClient.close()
                setCodeStore(null)
                setConnectionIssue({ nodeId: revoked.nodeId, name: revoked.name, reason: "revoked" })
                setAccessIssue(`Access to ${revoked.name} was revoked. Open a fresh pairing link from that computer to reconnect.`)
                return
            }
            if (fallback) {
                await switchRuntime(fallback.nodeId)
                const nextState = await backend.remove(revoked.nodeId)
                setComputerState(nextState)
                setScope(fallback.nodeId)
                window.localStorage.setItem(COMPUTER_SCOPE_KEY, fallback.nodeId)
                navigate(readComputerPaths()[fallback.nodeId] ?? codeRoutes.Code.makePath(), { replace: true })
                setComputerError(`Access to ${revoked.name} was revoked. Opened ${fallback.name} instead.`)
                return
            }

            active.store.disconnectAllStores()
            runtimeRef.current = null
            nestClient.close()
            setCodeStore(null)
            const nextState = await backend.remove(revoked.nodeId)
            setComputerState(nextState)
            setScope(nextState.computers.length > 0 ? nextState.activeNodeId : "")
            window.localStorage.removeItem(COMPUTER_SCOPE_KEY)
            setAccessIssue(`Access to ${revoked.name} was revoked. Open a fresh pairing link from that computer to reconnect.`)
        } catch (error) {
            setInitError(error instanceof Error ? error.message : String(error))
        } finally {
            recoveringUnauthorized.current = false
        }
    }, [backend, nativeMobile, navigate, switchRuntime])

    const recoverRemoteDisconnect = useCallback(async () => {
        const state = computerStateRef.current
        const active = runtimeRef.current
        if (!state || !active) return
        const disconnected = state.computers.find((computer) => computer.nodeId === active.nodeId)
        if (!disconnected || disconnected.kind !== "remote") return
        const fallback = state.computers.find((computer) => computer.kind === "local")
        if (!fallback) {
            setConnectionIssue({
                nodeId: disconnected.nodeId,
                name: disconnected.name,
                reason: "offline",
                failure: nestClient.getConnectionFailure(),
            })
            return
        }
        try {
            await switchRuntime(fallback.nodeId)
            setScope(fallback.nodeId)
            window.localStorage.setItem(COMPUTER_SCOPE_KEY, fallback.nodeId)
            navigate(readComputerPaths()[fallback.nodeId] ?? codeRoutes.Code.makePath(), { replace: true })
            setComputerError(`${disconnected.name} went offline. Opened ${fallback.name} instead.`)
            setOverviews((current) => ({
                ...current,
                [disconnected.nodeId]: offlineOverview(disconnected.nodeId, "Connection lost", current[disconnected.nodeId]),
            }))
        } catch (error) {
            setInitError(error instanceof Error ? error.message : String(error))
        }
    }, [navigate, switchRuntime])

    useEffect(() => {
        const clearDisconnectTimer = () => {
            if (!remoteDisconnectTimer.current) return
            globalThis.clearTimeout(remoteDisconnectTimer.current)
            remoteDisconnectTimer.current = null
        }
        const unsubscribe = nestClient.onStatusChange((status) => {
            if (status === "connected") {
                clearDisconnectTimer()
                setConnectionIssue(null)
                return
            }
            if (status === "unauthorized") {
                clearDisconnectTimer()
                setConnectionIssue(null)
                void recoverUnauthorizedConnection()
                return
            }
            if (status === "reconnecting" && !remoteDisconnectTimer.current) {
                remoteDisconnectTimer.current = globalThis.setTimeout(() => {
                    remoteDisconnectTimer.current = null
                    void recoverRemoteDisconnect()
                }, REMOTE_DISCONNECT_GRACE_MS)
            }
        })
        const unsubscribeFailure = nestClient.onConnectionFailure((failure) => {
            const nodeId = nestClient.getDiagnostics().nodeId
            setConnectionIssue((current) =>
                current?.reason === "offline" && current.nodeId === nodeId
                    ? {
                          ...current,
                          failure,
                      }
                    : current
            )
        })
        return () => {
            clearDisconnectTimer()
            unsubscribe()
            unsubscribeFailure()
        }
    }, [recoverRemoteDisconnect, recoverUnauthorizedConnection])

    useEffect(() => {
        if (!nativeMobile) return
        let disposed = false
        let listener: Awaited<ReturnType<typeof platform.addLifecycleListener>> | null = null

        const resumeActiveComputer = () => {
            if (foregroundRecovery.current) return foregroundRecovery.current
            const recovery = (async () => {
                const active = runtimeRef.current
                if (!active) return
                try {
                    const state = await backend.refreshAccess(active.nodeId)
                    if (!disposed) setComputerState(state)
                    const connection = await backend.getConnection(active.nodeId)
                    await nestClient.resume(connection)
                    await active.store.refreshRuntimeStateFromNest()
                    await liveStatusCoordinatorRef.current?.reconcileOnResume()
                    await pushEnrollment?.reconcileOnResume()
                    recordMobileReconciliationSuccess()
                    if (!disposed) setConnectionIssue(null)
                } catch (error) {
                    if (isNestUnauthorizedError(error)) {
                        await recoverUnauthorizedConnection()
                        return
                    }
                    const current = computerStateRef.current?.computers.find((computer) => computer.nodeId === active.nodeId)
                    if (!disposed && current) {
                        setConnectionIssue({
                            nodeId: current.nodeId,
                            name: current.name,
                            reason: "offline",
                            failure: nestClient.getConnectionFailure(),
                        })
                    }
                }
            })()
            foregroundRecovery.current = recovery
            void recovery.finally(() => {
                if (foregroundRecovery.current === recovery) foregroundRecovery.current = null
            })
            return recovery
        }

        void platform
            .addLifecycleListener((state) => {
                if (state === "background") {
                    nestClient.suspend()
                    return
                }
                void resumeActiveComputer()
            })
            .then((handle) => {
                if (disposed) void handle.remove()
                else listener = handle
            })

        return () => {
            disposed = true
            if (listener) void listener.remove()
        }
    }, [backend, nativeMobile, platform, pushEnrollment, recoverUnauthorizedConnection])

    const selectScope = useCallback(
        async (nextScope: ComputerScope): Promise<boolean> => {
            if (!computerState) return false
            setComputerError(null)
            try {
                if (!computerState.computers.some((computer) => computer.nodeId === nextScope)) throw new Error(`Computer ${nextScope} is not registered`)
                await switchRuntime(nextScope)
                setScope(nextScope)
                window.localStorage.setItem(COMPUTER_SCOPE_KEY, nextScope)
                const lastPath = readComputerPaths()[nextScope] ?? codeRoutes.Code.makePath()
                navigate(lastPath)
                return true
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error)
                setComputerError(message)
                setOverviews((current) => ({
                    ...current,
                    [nextScope]: offlineOverview(nextScope, message, current[nextScope]),
                }))
                return false
            }
        },
        [computerState, navigate, switchRuntime]
    )

    const addComputer = useCallback(
        async (input: AddRemoteComputerInput) => {
            const wasEmpty = !computerState || computerState.computers.length === 0
            const state = await backend.add(input)
            setComputerState(state)
            setAccessIssue(null)
            setConnectionIssue(null)
            if (wasEmpty) {
                await backend.activate(input.nodeId)
                try {
                    await initializeStore(input.nodeId)
                } catch (error) {
                    // The registry now has a computer but no store; without a
                    // visible issue the shell would sit on Loading forever.
                    setConnectionIssue({ nodeId: input.nodeId, name: input.name, reason: "offline", failure: nestClient.getConnectionFailure() })
                    throw error
                }
                setScope(input.nodeId)
                window.localStorage.setItem(COMPUTER_SCOPE_KEY, input.nodeId)
                navigate(codeRoutes.Code.makePath(), { replace: true })
            }
        },
        [backend, computerState, initializeStore, navigate]
    )

    const pairAndAdd = useCallback(
        async (invite: PairingInvite) => {
            setBusy(true)
            try {
                const paired = await pairWithComputer({
                    routes: invite.routes,
                    code: invite.code,
                    expectedNodeId: invite.nodeId,
                    clientName: nativeMobile ? `${PRODUCT_NAME} on ${platform.kind === "ios" ? "iOS" : "Android"}` : currentBrowserDeviceName(),
                    profile: currentProfile(),
                    share: invite.share,
                })
                const { access: _access, nodeName: _nodeName, ...registration } = paired
                await addComputer(registration)
                recordPerson(invite, paired)
                if (invite.pushClaim && pushEnrollment) {
                    try {
                        if (!paired.tokenId) throw new Error("The paired computer did not return a device identity")
                        await pushEnrollment.prepareClaim(paired.nodeId, paired.tokenId, invite.pushClaim)
                    } catch {
                        setComputerError("This computer paired successfully, but mobile notification enrollment needs to be retried with a new pairing link.")
                    }
                }
                void platform.haptic("success")
            } finally {
                setBusy(false)
            }
        },
        [addComputer, currentProfile, nativeMobile, platform, pushEnrollment, recordPerson]
    )

    const removeComputer = useCallback(
        async (nodeId: string) => {
            if (!computerState) return
            let removingOnlyActiveComputer = false
            if (runtimeRef.current?.nodeId === nodeId) {
                const fallback = computerState.computers.find((computer) => computer.nodeId !== nodeId)
                if (fallback) {
                    if (!(await selectScope(fallback.nodeId))) throw new Error("The fallback computer could not be opened")
                } else {
                    removingOnlyActiveComputer = true
                }
            }
            const state = await backend.remove(nodeId)
            setPeople(removePerson(nodeId))
            if (removingOnlyActiveComputer && runtimeRef.current) {
                runtimeRef.current.store.disconnectAllStores()
                runtimeRef.current = null
                nestClient.close()
                setCodeStore(null)
            }
            setComputerState(state)
            if (state.computers.length === 0) {
                setScope("")
                window.localStorage.removeItem(COMPUTER_SCOPE_KEY)
                setConnectionIssue(null)
            }
            setOverviews((current) => Object.fromEntries(Object.entries(current).filter(([id]) => id !== nodeId)))
        },
        [backend, computerState, selectScope]
    )

    const refreshAccess = useCallback(
        async (nodeId: string) => {
            setComputerState(await backend.refreshAccess(nodeId))
        },
        [backend]
    )

    const startPairing = useCallback(
        async (nodeId: string): Promise<PairingSession> => {
            if (!computerState) throw new Error("Computer registry is still loading")
            const latestState = await backend.refreshAccess(nodeId)
            setComputerState(latestState)
            const computer = latestState.computers.find((candidate) => candidate.nodeId === nodeId)
            if (!computer) throw new Error(`Computer ${nodeId} is not registered`)
            const connection = await backend.getConnection(nodeId)
            const routes = shareableNestRoutes(connection.routes)
            const primaryRoute = routes[0]
            if (!primaryRoute) {
                throw new Error(computer.access?.detail ?? "Configure a hosted relay or HTTPS private-network route before pairing another device")
            }
            const client = createNestClient()
            try {
                await connectNestConnection(client, connection, {
                    timeoutMs: CONNECT_TIMEOUT_MS,
                })
                const [pairing, publisherClaim] = await Promise.all([
                    client.requestMethod("auth.pairing.start", { ttlSeconds: 1_800 }, { timeoutMs: CONNECT_TIMEOUT_MS }),
                    createPublisherClaim(nodeId).catch(() => null),
                ])
                if (publisherClaim) liveStatusCoordinatorRef.current?.requestSync()
                return {
                    code: pairing.code,
                    endpoint: nestRouteLabel(primaryRoute),
                    expiresAt: pairing.expiresAt,
                    inviteUrl: createPairingInviteUrl({
                        routes,
                        code: pairing.code,
                        nodeId,
                        ...(publisherClaim ? { pushClaim: publisherClaim.claim } : {}),
                    }),
                    nodeId,
                    pushEnrollment: publisherClaim !== null,
                }
            } finally {
                client.close()
            }
        },
        [backend, computerState]
    )

    const startShare = useCallback(
        async (nodeId: string, request: ShareRequest): Promise<PairingSession> => {
            if (!computerState) throw new Error("Computer registry is still loading")
            const latestState = await backend.refreshAccess(nodeId)
            setComputerState(latestState)
            const computer = latestState.computers.find((candidate) => candidate.nodeId === nodeId)
            if (!computer) throw new Error(`Computer ${nodeId} is not registered`)
            const connection = await backend.getConnection(nodeId)
            const routes = shareableNestRoutes(connection.routes)
            const primaryRoute = routes[0]
            if (!primaryRoute) {
                throw new Error(computer.access?.detail ?? "Turn on Remote access for this computer before sharing a project")
            }
            const profile = currentProfile()
            const from = profile?.name?.trim() || computer.name
            const grants = grantsForLevel(request.level, request.workspaceIds, request.expiresAt)
            const client = createNestClient()
            try {
                await connectNestConnection(client, connection, { timeoutMs: CONNECT_TIMEOUT_MS })
                const pairing = await client.requestMethod(
                    "auth.pairing.start",
                    {
                        ttlSeconds: 1_800,
                        kind: "person",
                        displayName: request.personName,
                        grants,
                    },
                    { timeoutMs: CONNECT_TIMEOUT_MS }
                )
                // An older Nest ignores the grant list and would mint full admin.
                // It also omits principalId, which is the tell.
                if (!pairing.principalId)
                    throw new Error(`${computer.name} is running an older WildLark that cannot share projects yet. Restart it after updating.`)
                const share = {
                    from,
                    level: request.level,
                    ...(profile?.glyph ? { glyph: profile.glyph } : {}),
                    ...(profile?.color ? { color: profile.color } : {}),
                    ...(request.projectName ? { project: request.projectName } : {}),
                }
                const inviteUrl = createPairingInviteUrl({ routes, code: pairing.code, nodeId, share })
                return {
                    code: pairing.code,
                    endpoint: nestRouteLabel(primaryRoute),
                    expiresAt: pairing.expiresAt,
                    inviteUrl,
                    nodeId,
                    pushEnrollment: false,
                    share: {
                        ...share,
                        principalId: pairing.principalId,
                        message: shareMessage({ from, project: request.projectName, level: request.level, url: inviteUrl }),
                    },
                }
            } finally {
                client.close()
            }
        },
        [backend, computerState, currentProfile]
    )

    const startLink = useCallback(
        async (nodeId: string, personName: string) => {
            const connection = await backend.getConnection(nodeId)
            const routes = shareableNestRoutes(connection.routes)
            if (routes.length === 0) throw new Error("Turn on Remote access for this computer before linking a colleague")
            const profile = currentProfile()
            const computer = computerState?.computers.find((candidate) => candidate.nodeId === nodeId)
            const from = profile?.name?.trim() || computer?.name || nodeId
            const client = createNestClient()
            try {
                await connectNestConnection(client, connection, { timeoutMs: CONNECT_TIMEOUT_MS })
                const pairing = await client.requestMethod(
                    "auth.pairing.start",
                    { ttlSeconds: 1_800, kind: "person", displayName: personName, grants: [] },
                    { timeoutMs: CONNECT_TIMEOUT_MS }
                )
                if (!pairing.principalId) throw new Error(`${from} is running an older WildLark that cannot link colleagues yet. Restart it after updating.`)
                const invite = createPairingInviteUrl({
                    routes,
                    code: pairing.code,
                    nodeId,
                    share: { from, level: "none", ...(profile?.glyph ? { glyph: profile.glyph } : {}), ...(profile?.color ? { color: profile.color } : {}) },
                })
                return { invite, principalId: pairing.principalId, nodeId }
            } finally {
                client.close()
            }
        },
        [backend, computerState, currentProfile]
    )

    const retryInitialConnection = useCallback(
        async (issue: Extract<ConnectionIssue, { reason: "offline" }>) => {
            setBusy(true)
            try {
                nestClient.close()
                await backend.activate(issue.nodeId)
                await initializeStore(issue.nodeId)
                setComputerState(await backend.list())
                setScope(issue.nodeId)
                window.localStorage.setItem(COMPUTER_SCOPE_KEY, issue.nodeId)
                setConnectionIssue(null)
                setInitError(null)
                navigate(readComputerPaths()[issue.nodeId] ?? codeRoutes.Code.makePath(), { replace: true })
            } catch (error) {
                if (isNestUnauthorizedError(error)) {
                    setConnectionIssue({ nodeId: issue.nodeId, name: issue.name, reason: "revoked" })
                    setAccessIssue(`Access to ${issue.name} was revoked. Open a fresh pairing link from that computer to reconnect.`)
                } else {
                    setConnectionIssue({
                        ...issue,
                        failure: nestClient.getConnectionFailure() ?? issue.failure,
                    })
                }
            } finally {
                setBusy(false)
            }
        },
        [backend, initializeStore, navigate]
    )

    if (initError) {
        return (
            <div className="code-theme code-theme-black h-screen w-screen flex items-center justify-center bg-base-100 text-base-content p-6">
                <div className="max-w-xl space-y-3">
                    <div className="text-base font-medium">WildLark could not start</div>
                    <pre className="whitespace-pre-wrap rounded border border-border bg-base-200 p-3 text-xs text-muted">{initError}</pre>
                </div>
            </div>
        )
    }

    if (!computerState) {
        return (
            <div className="code-theme code-theme-black h-screen w-screen flex items-center justify-center bg-base-100 text-base-content">
                <div className="text-muted">Loading...</div>
            </div>
        )
    }

    const computerContext: ComputerContextValue = {
        backend,
        state: computerState,
        scope,
        overviews,
        overviewBatch,
        busy,
        error: computerError,
        people,
        selectScope,
        addComputer,
        pairAndAdd,
        removeComputer,
        refreshAccess,
        startPairing,
        startShare,
        startLink,
        refreshOverviews,
    }

    if (computerState.computers.length === 0) {
        return (
            <ComputerProvider value={computerContext}>
                <ConnectComputerPage inviteStatus={inviteStatus} accessIssue={accessIssue} />
            </ComputerProvider>
        )
    }

    if (connectionIssue) {
        if (connectionIssue.reason === "offline") {
            const retrying = runtimeRef.current?.nodeId === connectionIssue.nodeId
            return (
                <ComputerProvider value={computerContext}>
                    <ConnectionIssueView
                        computerName={connectionIssue.name}
                        failure={connectionIssue.failure}
                        retrying={retrying}
                        busy={busy}
                        onRetry={retrying ? undefined : () => void retryInitialConnection(connectionIssue)}
                        onForget={() => void removeComputer(connectionIssue.nodeId)}
                    />
                </ComputerProvider>
            )
        }
        return (
            <ComputerProvider value={computerContext}>
                <div className="code-theme code-theme-black flex h-screen w-screen items-center justify-center bg-base-100 p-6 text-base-content">
                    <div className="flex max-w-sm flex-col items-center text-center">
                        <div className="mb-5 flex h-12 w-12 items-center justify-center border border-border bg-base-200">
                            <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        </div>
                        <h1 className="text-lg font-semibold">{`Access to ${connectionIssue.name} was revoked`}</h1>
                        <p className="mt-2 text-sm leading-6 text-muted">
                            This phone no longer has access. Open a fresh pairing link on the computer to reconnect.
                        </p>
                        <button
                            type="button"
                            className="btn mt-5 h-9 border border-border px-3 text-sm text-muted hover:bg-base-200 hover:text-base-content"
                            onClick={() => void removeComputer(connectionIssue.nodeId)}
                        >
                            Forget this computer
                        </button>
                    </div>
                </div>
            </ComputerProvider>
        )
    }

    if (!codeStore) {
        return (
            <div className="code-theme code-theme-black h-screen w-screen flex items-center justify-center bg-base-100 text-base-content">
                <div className="text-muted">Loading...</div>
            </div>
        )
    }

    return (
        <ComputerProvider value={computerContext}>
            <CodeStoreProvider key={runtimeRef.current?.nodeId} store={codeStore}>
                <ThemedModalProvider>
                    <TaskCreateRoutes>
                        <Route path="/" element={<Navigate to={codeRoutes.Code.path} replace />} />
                        <Route path={codeRoutes.Computers.path} element={<Navigate to={codeRoutes.Code.path} replace />} />
                        <Route path={codeRoutes.Code.path} element={<CodeBaseRoute />} />
                        <Route path={codeRoutes.CodeWorkspaceCreate.path} element={<CodeWorkspaceCreateRoute />} />
                        <Route path={codeRoutes.CodeWorkspace.path} element={<CodeWorkspaceRoute />} />
                        <Route path={codeRoutes.CodeWorkspaceSettings.path} element={<CodeWorkspaceSettingsRoute />} />
                        <Route path={codeRoutes.CodeWorkspaceDiffLab.path} element={<CodeWorkspaceDiffLabRoute />} />
                        <Route path={codeRoutes.CodeWorkspaceTaskCreate.path} element={<CodeWorkspaceTaskCreateRoute />} />
                        <Route path={codeRoutes.CodeWorkspaceTaskCreating.path} element={<CodeWorkspaceTaskCreatingRoute />} />
                        <Route path={codeRoutes.CodeWorkspaceTask.path} element={<CodeWorkspaceTaskRoute />} />
                        <Route path="*" element={<Navigate to={codeRoutes.Code.path} replace />} />
                    </TaskCreateRoutes>
                    <ProductModuleGlobalSurfaces />
                </ThemedModalProvider>
            </CodeStoreProvider>
        </ComputerProvider>
    )
}

createRoot(document.getElementById("root")!).render(
    <BrowserRouter>
        <App />
        <VoiceCallPanel />
    </BrowserRouter>
)
